import { DiscountUpdateEvent } from '@theme/events';

/** @type {boolean} */
let isApplyingDiscount = false;

/** @type {WeakMap<HTMLElement, number>} */
const pendingTimeouts = new WeakMap();

const UPCART_REFRESH_MAX_ATTEMPTS = 15;
const UPCART_REFRESH_INTERVAL_MS = 200;
const UPCART_REGISTER_MAX_ATTEMPTS = 25;
const PENDING_FLAG_TIMEOUT_MS = 10000;

/**
 * Refreshes the UpCart drawer so discount totals appear without a page reload.
 */
function refreshUpcart() {
  if (typeof window.upcartRefreshCart === 'function') {
    window.upcartRefreshCart();
    return;
  }

  let attempts = 0;
  const interval = window.setInterval(() => {
    attempts += 1;
    if (typeof window.upcartRefreshCart === 'function') {
      window.upcartRefreshCart();
      window.clearInterval(interval);
    } else if (attempts >= UPCART_REFRESH_MAX_ATTEMPTS) {
      window.clearInterval(interval);
    }
  }, UPCART_REFRESH_INTERVAL_MS);
}

/**
 * @param {HTMLElement} section
 */
function clearPendingDiscount(section) {
  delete section.dataset.pendingDiscountApply;

  const timeoutId = pendingTimeouts.get(section);
  if (timeoutId) {
    window.clearTimeout(timeoutId);
    pendingTimeouts.delete(section);
  }
}

/**
 * @param {HTMLElement} section
 */
function markSectionPending(section) {
  section.dataset.pendingDiscountApply = 'true';

  const existingTimeout = pendingTimeouts.get(section);
  if (existingTimeout) {
    window.clearTimeout(existingTimeout);
  }

  pendingTimeouts.set(
    section,
    window.setTimeout(() => {
      clearPendingDiscount(section);
    }, PENDING_FLAG_TIMEOUT_MS)
  );
}

/**
 * @param {HTMLElement} section
 * @returns {boolean}
 */
function shouldTrackAddToCart(section, target) {
  const trigger = target.closest('[ref="addToCartButton"], .add-to-cart-button');
  if (!trigger || !section.contains(trigger)) return false;
  if (trigger instanceof HTMLAnchorElement && trigger.href) return false;
  if (trigger instanceof HTMLButtonElement && trigger.disabled) return false;

  return true;
}

/**
 * Applies a discount code to the cart and refreshes cart UI (UpCart + theme).
 *
 * @param {string} discountCode
 * @param {string} sectionId
 */
function applySectionDiscount(discountCode, sectionId) {
  if (!discountCode || isApplyingDiscount) return;

  isApplyingDiscount = true;

  fetch(window.Shopify.routes.root + 'cart/update.js', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      discount: discountCode,
    }),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error(`Discount request failed (${response.status})`);
      }
      return response.json();
    })
    .then((cart) => {
      refreshUpcart();
      document.dispatchEvent(new DiscountUpdateEvent(cart, sectionId));
    })
    .catch((error) => {
      console.error('Error applying discount:', error);
    })
    .finally(() => {
      isApplyingDiscount = false;
    });
}

/**
 * @param {HTMLElement} section
 */
function applyDiscountFromSection(section) {
  const discountCode = section.dataset.discountCode?.trim();
  if (!discountCode) return;

  clearPendingDiscount(section);
  applySectionDiscount(discountCode, section.id);
}

/**
 * @param {Event} event
 */
function handleFeaturedSectionAddIntent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const section = target.closest('featured-product-information[data-discount-enabled="true"]');
  if (!(section instanceof HTMLElement)) return;

  if (!shouldTrackAddToCart(section, target)) return;

  markSectionPending(section);
}

document.addEventListener('click', handleFeaturedSectionAddIntent, true);

document.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const section = form.closest('featured-product-information[data-discount-enabled="true"]');
    if (!(section instanceof HTMLElement)) return;

    const button = form.querySelector('[ref="addToCartButton"], .add-to-cart-button');
    if (!button || button instanceof HTMLAnchorElement) return;
    if (button instanceof HTMLButtonElement && button.disabled) return;

    markSectionPending(section);
  },
  true
);

document.addEventListener('cart:update', (event) => {
  if (event.detail?.data?.didError) return;
  if (event.detail?.data?.source !== 'product-form-component') return;

  const section = event.target?.closest?.('featured-product-information[data-discount-enabled="true"]');
  if (!(section instanceof HTMLElement)) return;

  applyDiscountFromSection(section);
});

document.addEventListener('cart:update', (event) => {
  if (!event.detail?.data?.didError) return;

  document
    .querySelectorAll('featured-product-information[data-pending-discount-apply="true"]')
    .forEach((section) => {
      if (section instanceof HTMLElement) {
        clearPendingDiscount(section);
      }
    });
});

function registerUpcartListener(attempt = 0) {
  if (typeof window.upcartSubscribeAddedToCart === 'function') {
    window.upcartSubscribeAddedToCart(() => {
      document
        .querySelectorAll('featured-product-information[data-discount-enabled="true"][data-pending-discount-apply="true"]')
        .forEach((section) => {
          if (section instanceof HTMLElement) {
            applyDiscountFromSection(section);
          }
        });
    });
    return;
  }

  if (attempt >= UPCART_REGISTER_MAX_ATTEMPTS) return;

  window.setTimeout(() => registerUpcartListener(attempt + 1), UPCART_REFRESH_INTERVAL_MS);
}

registerUpcartListener();
